/**
 * ACCOUNT NAV CUSTOM ELEMENT
 *
 * Mobile dropdown behaviour for the account sidebar navigation. The Liquid
 * renders a sticky current-page bar ([data-account-nav-toggle]) that toggles
 * the link list; open state is `data-ui="visible"` on the host (the theme's
 * visibility convention), styled via the group-data-visible/accnav Tailwind
 * variant. The host carries `data-body-lock`, so the global
 * body:has([data-body-lock][data-ui="visible"]) rule disables page scroll
 * while open. Closes on overlay tap, outside click and Escape. Desktop (lg:)
 * always shows the list.
 *
 * Usage (see snippets/account/account-nav.liquid):
 *   <x-account-nav class="group/accnav">
 *     <button data-account-nav-toggle aria-expanded="false">Current view</button>
 *     <nav>...</nav>
 *   </x-account-nav>
 */

export class AccountNav extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick)
    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleKeydown)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleKeydown)
  }

  get trigger(): HTMLElement | null {
    return this.querySelector('[data-account-nav-toggle]')
  }

  get isOpen(): boolean {
    return this.dataset.ui === 'visible'
  }

  handleClick = (event: Event) => {
    const toggle = (event.target as HTMLElement).closest('[data-account-nav-toggle]')
    if (toggle && this.contains(toggle)) {
      this.toggle()
    }
  }

  handleOutsideClick = (event: Event) => {
    if (this.isOpen && !this.contains(event.target as Node)) {
      this.close()
    }
  }

  handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
      this.trigger?.focus()
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.dataset.ui = 'visible'
    this.trigger?.setAttribute('aria-expanded', 'true')
  }

  close() {
    this.removeAttribute('data-ui')
    this.trigger?.setAttribute('aria-expanded', 'false')
  }
}
